import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import axios from 'axios';
import moment from 'moment';

// Libraries
import { getProcessEnv } from '@/lib/build';

// Project manifest
import pkg from '@/../package.json';

// Service options type
export type Options = {
  interval: number;
};

// Store state type
export type State = {
  version: string;
  next: string | null;
  lastCheck: string | null;
};

enum Mutation {
  SET__VERSION = 'SET__VERSION',
  SET__NEXT = 'SET__NEXT',
  SET__LAST_CHECK = 'SET__LAST_CHECK',
}

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 2.2.0
 * @version 1.0.0
 */
export default class UpdateService extends AbstractService<Options, State> {
  /**
   * @param options
   */
  constructor(options?: Options) {
    super(options, 'update');
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    this.$register(this.defaultState, {
      mutations: {
        [Mutation.SET__VERSION](state, version: State['version']) {
          Vue.set(state, 'version', version);
        },
        [Mutation.SET__NEXT](state, next: State['next']) {
          Vue.set(state, 'next', next);
        },
        [Mutation.SET__LAST_CHECK](state, lastCheck: State['lastCheck']) {
          Vue.set(state, 'lastCheck', lastCheck);
        },
      },
    });

    if (process.env.VUE_APP_BRANCH && process.env.VUE_APP_BRANCH !== 'local') {
      setTimeout(async () => {
        await this.fetchCurrentVersion();

        this.bulkAddEventListener(window, ['mousemove', 'keydown', 'mousedown', 'touchstart'], async () => {
          if (moment().diff(this.$state('lastCheck')) > this.options.interval) {
            await this.fetchCurrentVersion();
          }
        });
      });
    }
  }

  /**
   * Get default state of this store module
   */
  get defaultState(): State {
    return {
      version: this.getCurrentVersion(),
      next: null,
      lastCheck: null,
    };
  }

  getCurrentVersion() {
    let _v = pkg.version.split('.');
    const phase = process.env.VUE_APP_BRANCH || 'local';
    const hash = process.env.VUE_APP_HASH;

    if (phase !== 'production') {
      _v.push(phase);
    }
    if (hash) {
      _v.push(hash);
    }

    return _v.join('.');
  }

  public async fetchCurrentVersion() {
    this.logger.log('Check update...');

    const response = await axios.get(getProcessEnv('CLOUD_URL') + 'rest/versions?component=front');

    this.$commit(Mutation.SET__NEXT, response?.data.version);

    this.$commit(Mutation.SET__LAST_CHECK, moment().toISOString());
  }

  private bulkAddEventListener(object: any, events: any, callback: any) {
    events.forEach(function (event: any) {
      object.addEventListener(event, callback);
    });
  }

  private bulkRemoveEventListener(object: any, events: any, callback: any) {
    events.forEach(function (event: any) {
      object.removeEventListener(event, callback);
    });
  }

  public toSimple(versionSplitted: string[] | undefined) {
    if (!versionSplitted || !versionSplitted.length) return null;
    return `${versionSplitted[0]}.${versionSplitted[1]}.${versionSplitted[2]}`;
  }

  get version() {
    return this.$state<State['version']>('version');
  }

  get next() {
    return this.$state<State['next']>('next');
  }

  get lastCheck() {
    return this.$state<State['lastCheck']>('lastCheck');
  }

  get currentVersionNeedToDisplayDialog(): boolean {
    if (!this.version) return false;
    const simplifiedVersion = this.toSimple(this.version.split('.'));
    if (!simplifiedVersion) return false;

    return ['2.2.0'].includes(simplifiedVersion);
  }

  get updateAvailable() {
    if (getProcessEnv('IGNORE_UPDATE')) {
      this.logger.warn(`Update ignored : ${this.version} to ${this.next}`);
      return;
    }

    if (!this.next) return false;

    const simplifiedVersion: string = this.toSimple(this.version.split('.'))!;
    const simplifiedNext: string = this.toSimple(this.next.split('.'))!;

    switch (simplifiedVersion.localeCompare(simplifiedNext)) {
      case 1:
        this.logger.warn(`Rollback not allowed : ${this.version} to ${this.next}`);
        return false;
      case 0:
        this.logger.log(`Min version : ${this.version} to ${this.next}`);
        return this.next !== this.version;
      case -1:
        this.logger.log(`Maj version : ${this.version} to ${this.next}`);
        return true;
    }
  }
}
